import { createSlice } from '@reduxjs/toolkit';
import { CommandMocks } from 'src/App/Mocks/command.mocks';
import { LeaderboardMocks } from 'src/App/Mocks/leaderboard.mocks';
import { HomeTabs, Platforms } from 'src/App/Types/app.types';
import { ICommand } from 'src/App/Types/command.types';
import { ILeaderboard } from 'src/App/Types/leader.types';

export interface AppState {
	user_id: number | null;
	extensions_id: number | null;
	isDebug: boolean;
	hasSharedIdentity: boolean;
	platform: Platforms;
	status: 'loading' | 'ready' | 'failed';
	commands: ICommand[];
	leaderboard: ILeaderboard;
	currentCooldowns: {
		[key: string]: number;
	};
	tab_order: string[];
}

const initialState: AppState = {
	user_id: null, // Lumia's user id
	extensions_id: null, // Lumia's extension id
	isDebug: false,
	hasSharedIdentity: false,
	platform: Platforms.PANEL,
	status: 'loading',
	commands: [],
	leaderboard: {
		users: [],
		commands: [],
		pointUsers: [],
	},
	currentCooldowns: {},
	tab_order: [HomeTabs.COMMANDS, HomeTabs.HFX, HomeTabs.LEADERBOARD],
};

export const appSlice = createSlice({
	name: 'app',
	initialState,
	reducers: {
		updateStatus: (state, action) => {
			state.status = action.payload;
		},
		updateHasSharedIdentity: (state, action) => {
			state.hasSharedIdentity = action.payload;
		},
		updateDebug: (state, action) => {
			state.isDebug = action.payload;
			if (action.payload) {
				state.status = 'ready';
				state.commands = [...CommandMocks];
				state.leaderboard = { ...LeaderboardMocks };
			}
		},
		updatePlatform: (state, action) => {
			state.platform = action.payload;
		},
		retrievedAppState: (state, action) => {
			state.status = 'ready';
			state.user_id = action.payload.user_id;
			state.extensions_id = action.payload.id;
			state.commands = action.payload.commands;
			if (action.payload.tab_order) {
				state.tab_order = action.payload.tab_order;
			}
		},
		configRetrievalFailed: (state, err: any) => {
			state.status = 'failed';

			if (err && err.response && err.response.status === 400) {
				// Config not setup
			} else {
				// Error loading extension
			}
		},
		addCooldown: (state, action: { payload: { id: string; time: number } }) => {
			state.currentCooldowns[action.payload?.id] = action.payload.time;
		},
		removeCooldown: (state, action: { payload: { id: string } }) => {
			if (state.currentCooldowns[action.payload?.id]) {
				delete state.currentCooldowns[action.payload?.id];
			}
		},
		cooldownWentThrough: (state, action: { payload: { id: string } }) => {
			if (state.currentCooldowns[action.payload?.id]) {
				delete state.currentCooldowns[action.payload?.id];
			}
			if (state.currentCooldowns.channel) {
				delete state.currentCooldowns.channel;
			}
		},
		checkCooldowns: (state) => {
			const now = Date.now();
			Object.keys(state.currentCooldowns).map((id: string) => {
				if (state.currentCooldowns[id] < now) {
					delete state.currentCooldowns[id];
				}
			});
		},
	},
});

export const { updateStatus, updateHasSharedIdentity, updateDebug, updatePlatform, retrievedAppState, configRetrievalFailed, addCooldown, removeCooldown, cooldownWentThrough, checkCooldowns } =
	appSlice.actions;

export default appSlice.reducer;
